import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { CollectionContentSections } from '../../components/public-kb/CollectionContentSections'
import { ContributorRow } from '../../components/public-kb/ContributorRow'
import { PublicKbError } from '../../components/public-kb/PublicKbError'
import { PublicKbHero } from '../../components/public-kb/PublicKbHero'
import { usePageMeta } from '../../hooks/usePageMeta'
import { publicCollectionQueryOptions, usePublicCollection } from '../../hooks/usePublicKb'
import { isPublicKbHost } from '../../lib/public-kb-host'

export const Route = createFileRoute('/knowledge/c/$collectionSlug')({
  beforeLoad: async ({ context, params }) => {
    if (!isPublicKbHost()) {
      throw redirect({ to: '/' })
    }

    try {
      await context.queryClient.ensureQueryData(
        publicCollectionQueryOptions(params.collectionSlug),
      )
      return { collectionNotFound: false as const }
    } catch {
      return { collectionNotFound: true as const }
    }
  },
  component: PublicCollectionPage,
})

function PublicCollectionPage() {
  const { collectionSlug } = Route.useParams()
  const { publicWorkspace, collectionNotFound } = Route.useRouteContext()
  const { data: collection } = usePublicCollection(collectionSlug)

  usePageMeta(
    collection && publicWorkspace
      ? {
          title: `${collection.title} — ${publicWorkspace.name} Help Center`,
          description: `Browse ${collection.articleCount} article${collection.articleCount === 1 ? '' : 's'} in ${collection.title}.`,
          image: publicWorkspace.logo ?? publicWorkspace.headerImage?.url ?? undefined,
          url: typeof window !== 'undefined' ? window.location.href : undefined,
        }
      : null,
  )

  if (!publicWorkspace) return null

  if (collectionNotFound || !collection) {
    return (
      <PublicKbError
        title="Collection not found"
        message="This collection does not exist or is no longer available."
        action={
          <Link
            to="/knowledge"
            className="mt-6 inline-flex text-sm font-medium text-foreground underline-offset-4 hover:underline"
          >
            Back to help center
          </Link>
        }
      />
    )
  }

  return (
    <>
      <PublicKbHero
        title={collection.title}
        headerImage={publicWorkspace.headerImage}
        brandColor={publicWorkspace.brandColor}
        variant="collection"
      />
      <div className="mx-auto max-w-3xl px-4 pb-16 md:px-6">
        <ContributorRow
          authors={collection.authors}
          authorCount={collection.authorCount}
          articleCount={collection.articleCount}
        />
        <CollectionContentSections
          nestedCollections={collection.nestedCollections}
          directArticles={collection.directArticles}
        />
      </div>
    </>
  )
}
